import { useEffect } from 'react'
import { Outlet, Navigate, useParams, useLocation } from 'react-router-dom'
import i18n from '../../i18n'
import { setStoredLocale } from '../../lib/localeStorage'
import { normalizeRole } from '../../lib/rbac'
import { useAuthStore } from '../../store/authStore'
import useManagerStore from '../../store/managerStore'
import NoBranchAccessScreen from '../branch/NoBranchAccessScreen'
import AppSplashScreen from '../common/AppSplashScreen'
import { WsSessionProvider } from '../../lib/realtime/WsSessionContext'
import NotificationSync from './NotificationSync'

const VALID = new Set(['en', 'vi'])

const PUBLIC_SEGMENTS = new Set([
  'payment-success',
  'accept-invite',
  'login',
  'signup',
  'verify-otp',
  'forgot-password',
  'reset-password',
])

const REDIRECT_AUTHED_SEGMENTS = new Set([
  'login',
  'signup',
  'forgot-password',
  'reset-password',
])

function safeRelativeCallbackFromSearch(search) {
  const raw = new URLSearchParams(search).get('callback_url')?.trim() ?? ''
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return ''
  return raw
}

export default function LangLayout() {
  const { lang } = useParams()
  const location = useLocation()
  const token = useAuthStore((s) => s.token)
  const user = useAuthStore((s) => s.user)
  const branchRole = useManagerStore((s) => s.role)
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const branchBootstrapStatus = useAuthStore((s) => s.branchBootstrapStatus)
  const ensureBranchBootstrap = useAuthStore((s) => s.ensureBranchBootstrap)

  useEffect(() => {
    if (VALID.has(lang)) {
      void i18n.changeLanguage(lang)
      setStoredLocale(lang)
    }
  }, [lang])

  const segments = location.pathname.split('/').filter(Boolean)
  const firstRoute = segments[1] ?? ''
  const isPublicRoute = PUBLIC_SEGMENTS.has(firstRoute)
  const shouldRedirectAuthedUser = REDIRECT_AUTHED_SEGMENTS.has(firstRoute)
  const isOnboardingRoute = firstRoute === 'onboarding'
  const normalizedRole = normalizeRole(branchRole)
  const shouldForceOnboarding =
    token &&
    branchBootstrapStatus === 'ready' &&
    !isPublicRoute &&
    !isOnboardingRoute &&
    normalizedRole === 'admin' &&
    user?.is_onboarding === false

  useEffect(() => {
    if (!hasHydrated || !token || isPublicRoute) return
    void ensureBranchBootstrap()
  }, [hasHydrated, token, isPublicRoute, ensureBranchBootstrap])

  if (!VALID.has(lang)) {
    const parts = location.pathname.split('/').filter(Boolean)
    const rest = parts.slice(1).join('/')
    const path = rest ? `/${rest}` : '/dashboard'
    return <Navigate to={`/en${path}${location.search}${location.hash}`} replace />
  }

  if (!hasHydrated) {
    return (
      <AppSplashScreen
        message={i18n.t('branchGate.preparing')}
        description={i18n.t('branchGate.preparingDescription')}
      />
    )
  }

  if (!isPublicRoute && !token) {
    return (
      <Navigate
        to={`/${lang}/login`}
        replace
        state={{ from: location }}
      />
    )
  }

  if (shouldRedirectAuthedUser && token) {
    const nextFromCallback = safeRelativeCallbackFromSearch(location.search)
    if (nextFromCallback) {
      return <Navigate to={nextFromCallback} replace />
    }
    return <Navigate to={`/${lang}/dashboard`} replace />
  }

  if (shouldForceOnboarding) {
    return <Navigate to={`/${lang}/onboarding`} replace />
  }

  if (!isPublicRoute && token) {
    if (
      branchBootstrapStatus === 'idle' ||
      branchBootstrapStatus === 'loading'
    ) {
      return (
        <AppSplashScreen
          message={i18n.t('branchGate.loading')}
          description={i18n.t('branchGate.loadingDescription')}
        />
      )
    }

    if (
      branchBootstrapStatus === 'no_branch' ||
      branchBootstrapStatus === 'no_org' ||
      branchBootstrapStatus === 'error'
    ) {
      return <NoBranchAccessScreen />
    }
  }

  // Track B FE1.1 — mounted here (not in AppShell.jsx/DesktopShell.jsx,
  // which remount on every page navigation) so the WS session is
  // established once and survives SPA navigation for the whole
  // authenticated session. See WsSessionContext.jsx's docstring for why.
  return (
    <WsSessionProvider>
      <NotificationSync />
      <Outlet />
    </WsSessionProvider>
  )
}
